import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

export const useFetch = (url, options = {}) => {
  const [loading, setLoading] = useState(false);
  const [refetchToggle, setRefetch] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.user);
  const { identity } = user;
  const { skip } = options;

  const refetch = () => setRefetch((v) => !v);

  useEffect(() => {
    if (options.skip) return;

    // Two requests can be in flight on the same url, a refetch firing while the previous
    // one is still out. Whichever answers last would win, so an older answer could
    // overwrite a newer one, and a request outliving the component would write into a
    // caller that is gone. A superseded round trip drops its answer instead.
    let stale = false;

    setLoading(true);
    fetch(url, { ...options, headers: identity })
      .then((r) => {
        if (stale) return undefined;

        if (r.ok) {
          // Returned, so a body that fails to parse reaches the catch below instead of
          // rejecting on its own.
          return r.json().then((j) => {
            if (stale) return;

            setResponse(j);
            setError(null);
            setLoading(false);
          });
        } else {
          // Without this the caller is stuck on `loading` forever and never learns the
          // request failed, so a 404 reads exactly like a slow response.
          setError(r);
          setLoading(false);
        }
      })
      // The same dead end one step earlier: a network failure, or a body that is not
      // the JSON it claims to be, rejects instead of answering, and the caller waits on
      // a promise nothing will ever settle.
      .catch((reason) => {
        if (stale) return;

        setError(reason);
        setLoading(false);
      });

    return () => {
      stale = true;
    };
  }, [url, skip, identity, refetchToggle]);

  return [response, { error, refetch, loading }];
};
